import React, { useState, useRef, useEffect } from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const TimePicker = ({ value, onChange, disabled, label, error }) => {
    const { colors } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // Parse value (HH:mm)
    const [hours, minutes] = (value || '09:00').split(':');

    // Generate options
    const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
    const minuteOptions = Array.from({ length: 6 }, (_, i) => (i * 10).toString().padStart(2, '0'));

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleHourClick = (h) => {
        onChange(`${h}:${minutes}`);
        // Keep open so they can pick minutes too
    };

    const handleMinuteClick = (m) => {
        onChange(`${hours}:${m}`);
        setIsOpen(false);
    };

    // Scroll active item into view when opened
    useEffect(() => {
        if (isOpen) {
            const activeHour = document.getElementById(`hour-${hours}`);
            const activeMinute = document.getElementById(`minute-${minutes}`);
            if (activeHour) activeHour.scrollIntoView({ block: 'center' });
            if (activeMinute) activeMinute.scrollIntoView({ block: 'center' });
        }
    }, [isOpen, hours, minutes]);

    return (
        <div className="relative" ref={containerRef}>
            {label && (
                <label className="form-label">
                    {label}
                </label>
            )}

            <div
                className={`relative cursor-pointer`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <div className={`absolute left-3 top-2.5 ${colors.textMuted}`}>
                    <Clock className="w-4 h-4" />
                </div>
                <div className={`pl-10 pr-10 block w-full rounded-md border ${error ? 'border-red-300' : colors.inputBorder
                    } ${colors.inputBg} py-2 ${colors.inputText} shadow-sm transition-colors hover:border-blue-400`}>
                    {value || '--:--'}
                </div>
                <div className={`absolute right-3 top-2.5 ${colors.textMuted}`}>
                    <ChevronDown className="w-4 h-4" />
                </div>
            </div>

            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}

            {isOpen && !disabled && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-row h-64">
                    {/* Hours Column */}
                    <div className="flex-1 overflow-y-auto scrollbar-hide border-r border-gray-100 dark:border-gray-700">
                        <div className="text-xs font-semibold text-center py-2 bg-gray-50 dark:bg-gray-900 border-b text-gray-500 sticky top-0">HH</div>
                        {hourOptions.map(h => (
                            <div
                                key={h}
                                id={`hour-${h}`}
                                onClick={(e) => { e.stopPropagation(); handleHourClick(h); }}
                                className={`text-center py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 ${hours === h ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 font-bold' : colors.textPrimary}`}
                            >
                                {h}
                            </div>
                        ))}
                    </div>

                    {/* Minutes Column */}
                    <div className="flex-1 overflow-y-auto scrollbar-hide">
                        <div className="text-xs font-semibold text-center py-2 bg-gray-50 dark:bg-gray-900 border-b text-gray-500 sticky top-0">MM</div>
                        {minuteOptions.map(m => (
                            <div
                                key={m}
                                id={`minute-${m}`}
                                onClick={(e) => { e.stopPropagation(); handleMinuteClick(m); }}
                                className={`text-center py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 ${minutes === m ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 font-bold' : colors.textPrimary}`}
                            >
                                {m}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimePicker;
